import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import PayPalCheckoutButton from "../components/Cart/PayPalCheckoutButton";

const formatCurrency = new Intl.NumberFormat("es-MX", {
  style: "currency",
  currency: "MXN",
  minimumFractionDigits: 2,
});

const EcommerceCheckout = () => {
  const navigate = useNavigate();
  const {
    items,
    subtotal,
    shipping,
    total,
    iva,
    totalUnits,
    minimumOrderUnits,
    isMinimumOrderMet,
    validateCart,
    isValidating,
  } = useCart();
  const [errorMessage, setErrorMessage] = useState("");
  const [step, setStep] = useState(1);
  const [customer, setCustomer] = useState({
    email: "",
    country: "México",
    first_name: "",
    last_name: "",
    address: "",
    reference: "",
    postal_code: "",
    city: "",
    state: "",
    phone: "",
  });
  const [touched, setTouched] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);

  const validateOnceRef = useRef(false);
  useEffect(() => {
    if (!items.length || validateOnceRef.current) return;
    validateOnceRef.current = true;
    validateCart();
  }, [items, validateCart]);

  const validationErrors = useMemo(() => {
    const errors = {};
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customer.email);

    if (!customer.email) {
      errors.email = "Correo electrónico es obligatorio.";
    } else if (!emailOk) {
      errors.email = "Correo electrónico inválido.";
    }
    if (!customer.country) errors.country = "País es obligatorio.";
    if (!customer.first_name) errors.first_name = "Nombres es obligatorio.";
    if (!customer.last_name) errors.last_name = "Apellidos es obligatorio.";
    if (!customer.address) errors.address = "Dirección es obligatoria.";
    if (!customer.reference?.trim()) errors.reference = "Referencia es obligatoria.";
    if (!customer.postal_code) errors.postal_code = "Código postal es obligatorio.";
    if (!customer.city) errors.city = "Ciudad es obligatoria.";
    if (!customer.state) errors.state = "Estado es obligatorio.";
    if (!customer.phone) errors.phone = "Teléfono es obligatorio.";

    return errors;
  }, [customer]);

  const canContinue = useMemo(() => {
    const hasErrors = Object.keys(validationErrors).length > 0;
    if (hasErrors) {
      setErrorMessage("Completa todos los campos obligatorios.");
      return false;
    }
    setErrorMessage("");
    return true;
  }, [validationErrors]);

  const handleSuccess = useCallback(() => {
    setShowSuccess(true);
  }, []);

  return (
    <div className="min-h-screen bg-[#0f171a] text-white px-6 md:px-12 py-10">
      <div className="max-w-[1300px] mx-auto grid grid-cols-1 md:grid-cols-[1.2fr,1fr] gap-8">
        <div className="space-y-6">
          {step === 1 ? (
            <>
              <h2 className="text-2xl font-semibold">Contacto</h2>
              <input
                type="email"
                placeholder="Correo electrónico"
                value={customer.email}
                onChange={(event) => setCustomer({ ...customer, email: event.target.value })}
                onBlur={() => setTouched({ ...touched, email: true })}
                className="w-full px-4 py-3 rounded-md bg-white text-gray-900"
              />
              {touched.email && validationErrors.email && (
                <div className="text-sm text-red-300 font-semibold">{validationErrors.email}</div>
              )}

              <h2 className="text-2xl font-semibold">Entrega</h2>
              <input
                type="text"
                placeholder="País"
                value={customer.country}
                onChange={(event) => setCustomer({ ...customer, country: event.target.value })}
                onBlur={() => setTouched({ ...touched, country: true })}
                className="w-full px-4 py-3 rounded-md bg-white text-gray-900"
              />
              {touched.country && validationErrors.country && (
                <div className="text-sm text-red-300 font-semibold">{validationErrors.country}</div>
              )}
              <input
                type="text"
                placeholder="Nombres"
                value={customer.first_name}
                onChange={(event) => setCustomer({ ...customer, first_name: event.target.value })}
                onBlur={() => setTouched({ ...touched, first_name: true })}
                className="w-full px-4 py-3 rounded-md bg-white text-gray-900"
              />
              {touched.first_name && validationErrors.first_name && (
                <div className="text-sm text-red-300 font-semibold">{validationErrors.first_name}</div>
              )}
              <input
                type="text"
                placeholder="Apellidos"
                value={customer.last_name}
                onChange={(event) => setCustomer({ ...customer, last_name: event.target.value })}
                onBlur={() => setTouched({ ...touched, last_name: true })}
                className="w-full px-4 py-3 rounded-md bg-white text-gray-900"
              />
              {touched.last_name && validationErrors.last_name && (
                <div className="text-sm text-red-300 font-semibold">{validationErrors.last_name}</div>
              )}
              <input
                type="text"
                placeholder="Dirección"
                value={customer.address}
                onChange={(event) => setCustomer({ ...customer, address: event.target.value })}
                onBlur={() => setTouched({ ...touched, address: true })}
                className="w-full px-4 py-3 rounded-md bg-white text-gray-900"
              />
              {touched.address && validationErrors.address && (
                <div className="text-sm text-red-300 font-semibold">{validationErrors.address}</div>
              )}
              <input
                type="text"
                placeholder="Referencia de la dirección"
                value={customer.reference}
                onChange={(event) => setCustomer({ ...customer, reference: event.target.value })}
                onBlur={() => setTouched({ ...touched, reference: true })}
                className="w-full px-4 py-3 rounded-md bg-white text-gray-900"
              />
              {touched.reference && validationErrors.reference && (
                <div className="text-sm text-red-300 font-semibold">{validationErrors.reference}</div>
              )}
              <div className="grid grid-cols-3 gap-3">
                <input
                  type="text"
                  placeholder="Código postal"
                  value={customer.postal_code}
                  onChange={(event) => setCustomer({ ...customer, postal_code: event.target.value })}
                  onBlur={() => setTouched({ ...touched, postal_code: true })}
                  className="px-4 py-3 rounded-md bg-white text-gray-900"
                />
                <input
                  type="text"
                  placeholder="Ciudad"
                  value={customer.city}
                  onChange={(event) => setCustomer({ ...customer, city: event.target.value })}
                  onBlur={() => setTouched({ ...touched, city: true })}
                  className="px-4 py-3 rounded-md bg-white text-gray-900"
                />
                <input
                  type="text"
                  placeholder="Estado"
                  value={customer.state}
                  onChange={(event) => setCustomer({ ...customer, state: event.target.value })}
                  onBlur={() => setTouched({ ...touched, state: true })}
                  className="px-4 py-3 rounded-md bg-white text-gray-900"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  {touched.postal_code && validationErrors.postal_code && (
                    <div className="text-sm text-red-300 font-semibold">{validationErrors.postal_code}</div>
                  )}
                </div>
                <div>
                  {touched.city && validationErrors.city && (
                    <div className="text-sm text-red-300 font-semibold">{validationErrors.city}</div>
                  )}
                </div>
                <div>
                  {touched.state && validationErrors.state && (
                    <div className="text-sm text-red-300 font-semibold">{validationErrors.state}</div>
                  )}
                </div>
              </div>
              <input
                type="text"
                placeholder="Teléfono"
                value={customer.phone}
                onChange={(event) => setCustomer({ ...customer, phone: event.target.value })}
                onBlur={() => setTouched({ ...touched, phone: true })}
                className="w-full px-4 py-3 rounded-md bg-white text-gray-900"
              />
              {touched.phone && validationErrors.phone && (
                <div className="text-sm text-red-300 font-semibold">{validationErrors.phone}</div>
              )}

              {errorMessage && (
                <div className="text-sm text-red-300 font-semibold">{errorMessage}</div>
              )}
              <button
                type="button"
                onClick={() => setStep(2)}
                disabled={!canContinue}
                className="mt-2 bg-primary-green text-white font-bold px-6 py-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Siguiente
              </button>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-semibold">Pago</h2>
              <div className="space-y-4">
                <div className="bg-white text-gray-900 rounded-md px-4 py-3 flex items-center justify-between border-2 border-primary-green">
                  <div className="font-semibold">PayPal</div>
                  <span className="text-gray-500">🔒</span>
                </div>
                <div className="text-sm text-white/70">
                  Una vez que hagas clic en pagar, se te redireccionará a PayPal.
                </div>
                {!isMinimumOrderMet && (
                  <div className="text-sm font-semibold text-amber-300">
                    Pedido mínimo: {minimumOrderUnits} unidades. Actualmente tienes {totalUnits}.
                  </div>
                )}
                {isValidating && (
                  <div className="text-sm text-white/70">Validando carrito...</div>
                )}
                <PayPalCheckoutButton customer={customer} onSuccess={handleSuccess} />
              </div>
              <button
                type="button"
                onClick={() => setStep(1)}
                className="mt-4 border border-white/30 text-white font-bold px-6 py-3 rounded-lg"
              >
                Regresar
              </button>
            </>
          )}
        </div>

        <div className="border-l border-white/20 pl-6 space-y-4">
          <h2 className="text-3xl font-bold">RESUMEN</h2>
          <div className="space-y-3">
            {items.map((item) => (
              <div key={item.key} className="flex items-start justify-between">
                <div className="text-sm text-left">
                  <div className="font-semibold">{item.name}</div>
                  <div className="text-white/70">x{item.quantity}</div>
                </div>
                <div className="text-sm font-semibold">
                  {formatCurrency.format((item.unitPrice || 0) * item.quantity)}
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-white/20 pt-4 space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span>Envío</span>
              <span>{shipping === 0 ? "GRATIS" : formatCurrency.format(shipping)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>IVA (incluido)</span>
              <span>{formatCurrency.format(iva)}</span>
            </div>
            <div className="flex items-center justify-between text-lg font-bold pt-3">
              <span>Total</span>
              <span>{formatCurrency.format(total || subtotal + shipping)}</span>
            </div>
          </div>
        </div>
      </div>
      {showSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-white text-gray-900 rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
            <div className="flex justify-center mb-4">
              <img
                src="/images/logo.png"
                alt="Omova"
                className="block h-16 w-auto object-contain object-center"
              />
            </div>
            <h3 className="text-xl font-bold mb-2">Pago confirmado</h3>
            <p className="text-sm text-gray-600 mb-6">
              Tu compra fue registrada correctamente. En breve recibirás un correo con
              la confirmación.
            </p>
            <button
              type="button"
              onClick={() => {
                setShowSuccess(false);
                navigate("/ecommerce");
              }}
              className="bg-primary-green text-white font-bold px-6 py-2 rounded-lg"
            >
              Continuar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EcommerceCheckout;
